"use client";

import type { CalendarViewPreferences } from "@workspace/types/src/entities/user";
import { useCallback, useEffect, useState } from "react";
import {
	DEFAULT_CALENDAR_PREFERENCES,
	useCalendarPreferences,
} from "@/hooks/shared/use-calendar-preferences";
import { trpc } from "@/lib/trpc/react";

/**
 * Custom hook for managing calendar preferences in festival pages
 * Handles local state, server sync, and reset functionality
 */
export function useFestivalCalendarPreferences() {
	// Local preferences state for instant UI updates
	const [localCalendarPreferences, setLocalCalendarPreferences] = useState<
		CalendarViewPreferences | undefined
	>(undefined);

	// Server preferences
	const {
		preferences: serverCalendarPreferences,
		refetch: refetchCalendarPreferences,
	} = useCalendarPreferences();

	// Use local preferences if set, otherwise fall back to server preferences
	const calendarPreferences =
		localCalendarPreferences ?? serverCalendarPreferences;

	// Initialize local preferences from server
	useEffect(() => {
		// Always sync with server preferences on mount/when server data changes
		// This ensures we get the latest saved preferences after page refresh
		if (serverCalendarPreferences) {
			setLocalCalendarPreferences(serverCalendarPreferences);
		}
	}, [serverCalendarPreferences]);

	// tRPC mutation for calendar preferences (used for reset)
	const updateCalendarPreferencesMutation =
		trpc.settings.updateCalendarPreferences.useMutation({
			onSuccess: () => {
				refetchCalendarPreferences();
				// Clear local state to let server preferences take over after reset
				setLocalCalendarPreferences(undefined);
			},
		});

	// Handle calendar preferences reset
	const handleResetCalendarPreferences = useCallback(() => {
		updateCalendarPreferencesMutation.mutate({
			calendarViewPreferences: DEFAULT_CALENDAR_PREFERENCES,
		});
	}, [updateCalendarPreferencesMutation]);

	// Handle calendar preferences save (for instant UI updates)
	const handleCalendarPreferencesSaved = useCallback(
		async (prefs: CalendarViewPreferences) => {
			// Update local state immediately for instant UI update
			setLocalCalendarPreferences(prefs);
			// Also refetch from server for consistency
			await refetchCalendarPreferences();
		},
		[refetchCalendarPreferences],
	);

	return {
		calendarPreferences,
		handleResetCalendarPreferences,
		handleCalendarPreferencesSaved,
		setLocalCalendarPreferences,
	};
}
